import * as z from 'zod/mini';
import {
  COORDINATE_DISPLAY_DIGITS,
  LATITUDE_MAX_DEGREES,
  LONGITUDE_MAX_DEGREES,
  PHOTO_ARCHIVE_REASON_MAX_LENGTH,
  PHOTO_CAPTION_MAX_LENGTH,
  PHOTO_CONTENT_TYPES,
  PHOTO_TAG_MAX_LENGTH,
  PHOTO_TAGS_MAX,
  TAKEN_AT_DISPLAY_LENGTH,
} from '../constants.ts';
import { type DirectionsLink, directionsLinks } from '../directions.ts';
import { idSchema, NODE_TYPES, pathSchema, timestampSchema } from './common.ts';

// Photos attach to any node: a client (the signed contract, the site
// survey), a property, a room, a rack, an item, or a cable drop (the jack
// as terminated, the tester's screen) — brief, journey 3. The Worker
// derives the content type and pixel size from the uploaded bytes, so a
// photo's metadata is trustworthy even though the upload came from a
// browser.
//
// A photo is never quietly lost: instead of a delete it is archived, with
// who did it, when, and why, and with the name and path of the node it hung
// off captured at that moment so the record still reads once the node is
// gone (deleting a node archives every photo beneath it). Only a manager
// removes an archived photo for good.

export const PHOTO_NODE_TYPES = NODE_TYPES;
export type PhotoNodeType = (typeof PHOTO_NODE_TYPES)[number];
export const photoNodeTypeSchema = z.enum(PHOTO_NODE_TYPES);

export const photoContentTypeSchema = z.enum(PHOTO_CONTENT_TYPES);
export type PhotoContentType = z.infer<typeof photoContentTypeSchema>;

export const captionSchema = z.string().check(z.trim(), z.maxLength(PHOTO_CAPTION_MAX_LENGTH));

/**
 * One tag: a short label such as "before" or "rack rear". Blank tags are a
 * typing artefact rather than a label, so the array schema drops them before
 * this runs; anything left has to be short enough to read in a list.
 */
export const photoTagSchema = z
  .string()
  .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(PHOTO_TAG_MAX_LENGTH));

/** A photo's tags, bounded so one photo cannot carry a paragraph of them. */
export const photoTagsSchema = z.array(photoTagSchema).check(z.maxLength(PHOTO_TAGS_MAX));

/**
 * Where a photo was taken (ROADMAP L1, migration 0060): the browser's own
 * Geolocation reading at the moment of capture, asked for once per session and
 * never waited on. The two coordinates travel as one object because half a
 * point is not a place — the column CHECKs bound each number, and this is what
 * makes them a pair.
 */
export const photoPointSchema = z.object({
  latitude: z.number().check(z.gte(-LATITUDE_MAX_DEGREES), z.lte(LATITUDE_MAX_DEGREES)),
  longitude: z.number().check(z.gte(-LONGITUDE_MAX_DEGREES), z.lte(LONGITUDE_MAX_DEGREES)),
  /** The radius of uncertainty the browser reported, in metres; null when it gave none. */
  accuracyMeters: z.nullable(z.number().check(z.gte(0))),
});
export type PhotoPoint = z.infer<typeof photoPointSchema>;

export const photoSchema = z.object({
  id: idSchema,
  nodeType: photoNodeTypeSchema,
  nodeId: idSchema,
  contentType: photoContentTypeSchema,
  sizeBytes: z.int().check(z.positive()),
  width: z.int().check(z.positive()),
  height: z.int().check(z.positive()),
  caption: captionSchema,
  /**
   * EXIF DateTimeOriginal from the picked file, read in the browser before
   * the re-encode strips every other scrap of metadata; null when the file
   * carried none (a screenshot, a re-saved image, a camera with no clock).
   */
  takenAt: z.nullable(timestampSchema),
  /** Short labels typed on the upload panel ("before", "rack rear"). */
  tags: photoTagsSchema,
  /** Where the device was when the photo was taken; null when no position was allowed or found. */
  point: z.nullable(photoPointSchema),
  createdAt: timestampSchema,
  /** When the photo was archived; null while it is current. */
  archivedAt: z.nullable(timestampSchema),
  /** The account that archived it (no longer looked up, so it survives the account). */
  archivedBy: z.nullable(idSchema),
  archivedByUsername: z.string(),
  archiveReason: z.string(),
  /** The node's name and path from the client down, as they were when archived; empty while current. */
  nodeName: z.string(),
  path: pathSchema,
});
export type Photo = z.infer<typeof photoSchema>;

export const photoPatchSchema = z.strictObject({ caption: captionSchema });
export type PhotoPatch = z.infer<typeof photoPatchSchema>;

/** The body of POST /api/photos/:id/archive: a reason is always required. */
export const archiveInputSchema = z.strictObject({
  reason: z
    .string()
    .check(z.trim(), z.minLength(1, 'Required'), z.maxLength(PHOTO_ARCHIVE_REASON_MAX_LENGTH)),
});
export type ArchiveInput = z.infer<typeof archiveInputSchema>;

export const photoListSchema = z.object({ photos: z.array(photoSchema) });
export const photoEnvelopeSchema = z.object({ photo: photoSchema });

/** An archived photo as a client's archive lists it: it says whether its node is still there to restore to. */
export const archivedPhotoSchema = z.extend(photoSchema, { nodeExists: z.boolean() });
export type ArchivedPhoto = z.infer<typeof archivedPhotoSchema>;
export const archivedPhotoListSchema = z.object({ photos: z.array(archivedPhotoSchema) });

/**
 * One photo on a property's timeline (ROADMAP L2): the photo itself, the node
 * it hangs off named as that node's page names it, the room it sits under (so
 * the timeline can be narrowed to one), and the instant it is ordered by.
 *
 * `at` is the taken-at date when the camera recorded one and the upload time
 * otherwise. The Worker works it out once, so the page, the day headings, and
 * the before/after pairing all order on the same value instead of each
 * deciding the fallback again.
 */
export const photoTimelineEntrySchema = z.object({
  photo: photoSchema,
  /** The room, rack, device, or run the photo hangs off, by name. */
  placeName: z.string(),
  /** The room it sits under; null for a photo on the property itself. */
  roomId: z.nullable(idSchema),
  /** That room's name; '' when there is no room. */
  roomName: z.string(),
  at: timestampSchema,
});
export type PhotoTimelineEntry = z.infer<typeof photoTimelineEntrySchema>;

export const photoTimelineSchema = z.object({
  entries: z.array(photoTimelineEntrySchema),
  /** How many photos past PHOTO_TIMELINE_LIMIT the property holds; 0 when none were left out. */
  omitted: z.int().check(z.gte(0)),
});

/**
 * The instant a browser reports for EXIF DateTimeOriginal, as the upload
 * sends it: an ISO string the Date constructor round-trips, or empty for
 * "the file carried no date". Anything else is a client that got it wrong
 * and is refused rather than stored as an unreadable timestamp.
 */
function isIsoInstant(value: string): boolean {
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.toISOString() === value;
}

export const takenAtFieldSchema = z
  .string()
  .check(z.refine((value) => value === '' || isIsoInstant(value), 'An ISO timestamp, or empty'));

/**
 * The `point` part of an upload: the position object as JSON, or empty for
 * "no position was allowed or found". An unparseable value is refused rather
 * than dropped, the same way the tags field is, so a client that meant to send
 * a reading and got the encoding wrong hears about it.
 */
export const pointFieldSchema = z.nullable(photoPointSchema);

/**
 * Multipart field names of an upload: the resized image, its thumbnail, an
 * optional caption, the EXIF date the browser read before it re-encoded,
 * the tags as a JSON array, and whether the image carries annotation the
 * person drew on it (which the change history says).
 */
export const PHOTO_FORM_FIELDS = {
  file: 'file',
  thumb: 'thumb',
  caption: 'caption',
  takenAt: 'takenAt',
  tags: 'tags',
  annotated: 'annotated',
  /** Where the device was at capture, as JSON; empty when no position was allowed or found. */
  point: 'point',
  /** The id the client already gave the photo, so a queued upload replayed twice files one. */
  id: 'id',
} as const;

/** The `annotated` field's only accepted value; absent means "as it was picked". */
export const PHOTO_ANNOTATED_FLAG = '1';

/**
 * "Taken 2026-09-03 14:12", or "Date unknown" for a photo whose file
 * carried no EXIF DateTimeOriginal. The stored instant is the camera's own
 * wall-clock reading kept as UTC (see TAKEN_AT_DISPLAY_LENGTH), so it is
 * shown back exactly as the camera recorded it, in the app and the report
 * alike.
 */
export function formatTakenAt(takenAt: string | null): string {
  if (takenAt === null) {
    return 'Date unknown';
  }
  return `Taken ${takenAt.slice(0, TAKEN_AT_DISPLAY_LENGTH).replace('T', ' ')}`;
}

/**
 * "37.77493, -122.41942 ±12 m", or "Location not recorded" for a photo taken
 * without one. The reading is shown exactly as the browser gave it — the app
 * never rounds a position into a claim the device did not make — and the
 * accuracy travels with it so an indoor fix is not read as a survey mark.
 */
export function formatPhotoPoint(point: PhotoPoint | null): string {
  if (point === null) {
    return 'Location not recorded';
  }
  const at = `${point.latitude.toFixed(COORDINATE_DISPLAY_DIGITS)}, ${point.longitude.toFixed(COORDINATE_DISPLAY_DIGITS)}`;
  return point.accuracyMeters === null
    ? at
    : `${at} ±${String(Math.round(point.accuracyMeters))} m`;
}

/**
 * Maps links for a recorded position, built by the same helper a property's
 * directions use, so a photo's point opens the phone's own maps app exactly
 * as an address does. A point carries no address text, only the coordinates.
 */
export function photoPointLinks(point: PhotoPoint): DirectionsLink[] {
  return directionsLinks({
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    latitude: point.latitude,
    longitude: point.longitude,
  });
}

/** URL paths of a photo's stored bytes; both require the session cookie. */
export function photoFileHref(id: string): string {
  return `/api/photos/${id}/file`;
}

export function photoThumbHref(id: string): string {
  return `/api/photos/${id}/thumb`;
}
